import type { ProductCustomField } from '@/types/allocationType'
import type { ClientType } from '@/types/clientType'
import type { ProductType, AllocationProduct } from '@/types/productType'
import type { AllocationSupplier } from '@/types/supplierType'
import type { MunicipalityType } from '@/types/municipalityType'
import type { FileType } from '@/types/fileType'
import type { EntityRelation, Signals } from '@/types/types'

export type AllocationRequestType = {
  id: string
  createdAt: string
  updatedAt: string
  processedAt: string
  client?: Pick<
    ClientType,
    | 'id'
    | 'initials'
    | 'middlename'
    | 'lastname'
    | 'gender'
    | 'bsn'
    | 'birthDate'
  >
  bsn?: string
  product?: Omit<AllocationProduct, 'labels'> &
    Pick<ProductType, 'unit' | 'frequency'> & {
      price: ProductType['currentPrice']
    }
  supplier: Pick<AllocationSupplier, 'id' | 'name' | 'agbCode'>
  municipality: Pick<MunicipalityType, 'id' | 'name' | 'wmoCode'>
  unit: ProductType['unit']
  frequency: ProductType['frequency']
  wmoCode?: string
  wmoCategory: string
  allocation?: {
    id: string
  }
  allocationRequestStatus: AllocationRequestStatus
  denyReason?: AllocationRequestDenyReason
  denyComment?: string
  referrerName?: string
  referrerFunctionType: ReferrerFunctionTypeOf
  supplierReference: string
  referrerComment?: string // Should be `supplierComment` or just `comment`?
  size: number
  since: string
  until?: string
  files?: Array<FileType>
  jw: boolean
  productCustomFields?: ProductCustomField[]
  convertToAllocation: boolean
  relations?: EntityRelation[]
  signals?: Signals
}

export type AllocationRequestDTO = Pick<
  AllocationRequestType,
  | 'since'
  | 'until'
  | 'productCustomFields'
  | 'referrerFunctionType'
  | 'supplierReference'
  | 'referrerName'
  | 'referrerComment'
> & {
  client?: ClientType['id']
  bsn?: ClientType['bsn']
  product?: ProductType['id']
  supplier: AllocationSupplier['id']
  volume: number
  files?: Array<string>
}

export type AllocationRequestPut = AllocationRequestDTO &
  Pick<
    AllocationRequestType,
    'id' | 'allocationRequestStatus' | 'denyReason' | 'denyComment'
  >

export enum AllocationRequestStatus {
  REQUESTED_AT_MUNICIPALITY = 'requested_at_municipality',
  REQUESTED_AT_SUPPLIER = 'requested_at_supplier',
  APPROVED = 'approved',
  DENIED = 'denied',
  PARKED = 'parked',
}

export enum AllocationRequestDenyReason {
  NEW_ALLOCATION = 'new_allocation',
  OUT_OF_BUDGET = 'out_of_budget',
  NO_CONTRACT = 'no_contract',
  OUTSIDE_OF_CONTRACT = 'outside_of_contract',
  DIFFERENT_MUNICIPALITY = 'different_municipality',
  STACKING = 'stacking',
  HEALTHCARE_SPECIFIC = 'healthcare_specific',
  RESIDENCE_PRINCIPLE = 'residence_principle',
  AGE_BARRIER_REACHED = 'age_barrier_reached',
}

export enum ReferrerFunctionTypeOf {
  MUNICIPALITY = 'municipality',
  GENERAL_PRACTITIONER = 'general_practitioner',
  YOUTH_DOCTOR = 'youth_doctor',
  CERTIFIED_INSTITUTION = 'certified_institution',
  MEDICAL_SPECIALIST = 'medical_specialist',
  NO_REFERRER = 'no_referrer',
  JUDGE_PUBLIC_PROSECUTOR_YOUTH_INSTITUTION_OFFICER = 'judge_public_prosecutor_youth_institution_officer',
}
